'use client';


import dynamic from 'next/dynamic';
import Icon from '@/components/ui/AppIcon';
import Footer from '@/components/common/Footer';
import Link from 'next/link';
import Navbar from '@/components/common/Navbar';

// Dynamically import the map component to avoid SSR issues with Leaflet
const ServiceAreaMap = dynamic(() => import('./ServiceAreaMap'), {
  ssr: false,
  loading: () => (
    <div className="h-96 flex items-center justify-center bg-gray-100 rounded-xl">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading map...</p>
      </div>
    </div>
  )
});

export default function ServiceAreasPage() {
  const serviceAreas = [
    { name: 'Minneapolis', icon: 'MapPinIcon' as const },
    { name: 'St. Paul', icon: 'MapPinIcon' as const },
    { name: 'Bloomington', icon: 'MapPinIcon' as const },
    { name: 'Eden Prairie', icon: 'MapPinIcon' as const },
    { name: 'Minnetonka', icon: 'MapPinIcon' as const },
    { name: 'Edina', icon: 'MapPinIcon' as const },
    { name: 'Burnsville', icon: 'MapPinIcon' as const },
    { name: 'Plymouth', icon: 'MapPinIcon' as const },
    { name: 'Maple Grove', icon: 'MapPinIcon' as const },
    { name: 'Woodbury', icon: 'MapPinIcon' as const },
    { name: 'Lakeville', icon: 'MapPinIcon' as const },
    { name: 'Eagan', icon: 'MapPinIcon' as const }
  ];

  return (
    <main className="min-h-screen bg-gradient-to-b from-white to-green-50/30">
      <Navbar />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-600 to-green-800 text-white py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <Link href="/homepage" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
              <Icon name="ArrowLeftIcon" size={20} />
              <span>Back to Home</span>
            </Link>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">Service Areas</h1>
            <p className="text-xl text-white/90">Proudly serving the Twin Cities metro area with professional cleaning services.</p>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 section-gradient-mid dot-pattern">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-foreground mb-4">Twin Cities Coverage Map</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We provide comprehensive cleaning services throughout the Minneapolis-St. Paul metropolitan area.
            </p>
          </div>

          {/* Leaflet Map */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <ServiceAreaMap />
          </div>
        </div>
      </section>

      {/* Cities List */}
      <section className="py-20 bg-gradient-to-br from-[#2E7D32]/10 via-green-50 to-[#4CAF50]/10">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-foreground mb-4">Cities We Serve</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our professional cleaning team is ready to serve you in these locations and surrounding areas.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {serviceAreas.map((area) => (
              <div
                key={area.name}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow flex items-center gap-3"
              >
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name={area.icon} size={20} className="text-green-600" />
                </div>
                <span className="font-semibold text-foreground">{area.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Details */}
      <section className="py-20 section-gradient-warm stripe-pattern">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="MapIcon" size={32} className="text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">40+ Miles</h3>
                <p className="text-muted-foreground">Service Radius</p>
              </div>
              <div>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="HomeIcon" size={32} className="text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">12+ Cities</h3>
                <p className="text-muted-foreground">Covered Areas</p>
              </div>
              <div>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="TruckIcon" size={32} className="text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">Same Day</h3>
                <p className="text-muted-foreground">Available in Most Areas</p>
              </div>
            </div>

            <div className="mt-12 bg-green-50 p-8 rounded-xl">
              <h3 className="text-2xl font-bold text-foreground mb-4 text-center">Do not See Your City?</h3>
              <p className="text-muted-foreground text-center mb-6">
                We are constantly expanding our service area. Contact us to check if we can serve your location.
              </p>
              <div className="text-center">
                <Link
                  href="/contact"
                  className="inline-flex items-center gap-2 bg-green-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-700 transition-all shadow-lg hover:shadow-xl"
                >
                  Contact Us
                  <Icon name="ArrowRightIcon" size={20} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}